/**
 * DataSourceWizard - Complete data source configuration wizard with all forms and upload functionality
 * Extracted from the original OnboardingPage with full functionality preserved
 */

import React, { useState, useEffect, useRef } from 'react';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useOAuth } from '../../hooks/useOAuth';
import { useDataPreview } from '../../hooks/useDataPreview';
import { useMarketData } from '../../hooks/useMarketData';
import { API_CONFIG } from '../../config/api-config';
import ClientSelector from './ClientSelector';
import { ClientCreateForm } from '../clients/ClientCreateForm';

interface ConnectionProvider {
  id: string;
  name: string;
  icon: string;
  bgColor: string;
  description: string;
}

interface DataSourceWizardProps {
  sourceId: string;
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  onSkip: () => void;
  stepNum: number;
  totalSteps: number;
  nextSourceTitle?: string;
}

const DataSourceWizard: React.FC<DataSourceWizardProps> = ({
  sourceId,
  isOpen,
  onClose,
  onComplete,
  onSkip,
  stepNum,
  totalSteps,
  nextSourceTitle
}) => {
  const { dataSources, setConnectedSources, setMarketStats, selectedClient, clients } = useOnboarding();
  // TASK-630: on a fresh tenant there is no client to select — offer inline creation
  // instead of the dead-end "select a client" toast.
  const [showCreateClient, setShowCreateClient] = useState(false);
  const source = dataSources.find(s => s.id === sourceId);

  // Track active polling intervals to prevent memory leaks
  const activePollingIntervals = useRef<Set<NodeJS.Timeout>>(new Set());

  // File upload states
  const [_uploadId, setUploadId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'processing' | 'ready' | 'error'>('idle');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [autoAdvanceTimer, setAutoAdvanceTimer] = useState<NodeJS.Timeout | null>(null);
  const [processingState, setProcessingState] = useState<Record<string, 'idle' | 'processing' | 'complete'>>({});

  // OAuth configuration state
  const [oauthConfig, setOauthConfig] = useState({
    clientId: '',
    redirectUri: `${API_CONFIG.FRONTEND_URL}/auth/oauth/microsoft/callback`,
    tenantId: 'common'
  });
  const [oauthConfigLoaded, setOauthConfigLoaded] = useState(false);
  const [showOAuthConfig, setShowOAuthConfig] = useState(false);

  // Market data state (replacing manual Finnhub state)
  const [showMarketDataConfig, setShowMarketDataConfig] = useState(false);
  const [marketDataConfig, setMarketDataConfig] = useState({
    apiKey: '',
    planTier: 'free' as 'free' | 'basic' | 'premium'
  });

  // OAuth integration
  const {
    isAuthenticated: isOAuthConnected,
    user: oauthUser,
    connect: connectOAuth,
    disconnect: disconnectOAuth,
    isConnecting: isOAuthConnecting,
    error: oauthError,
    clearError: clearOAuthError
  } = useOAuth();

  // Market data integration (following same pattern as OAuth)
  const {
    isConnected: isMarketDataConnected,
    config: marketDataConnectionConfig,
    connect: connectMarketData,
    disconnect: disconnectMarketData,
    isConnecting: isMarketDataConnecting,
    error: marketDataError,
    clearError: clearMarketDataError
  } = useMarketData();

  // Data preview integration
  const {
    fetchDataPreview,
    clearError: clearDataPreviewError
  } = useDataPreview();

  // CRM providers
  const crmProviders: ConnectionProvider[] = [
    { id: 'salesforce', name: 'Salesforce', icon: '☁️', bgColor: 'bg-blue-500', description: 'Financial Services Cloud' },
    { id: 'hubspot', name: 'HubSpot', icon: '🔶', bgColor: 'bg-orange-600', description: 'Professional/Enterprise' }
  ];

  // Market data error is now handled by useMarketData hook

  // Load OAuth configuration when email sidebar is opened
  useEffect(() => {
    const loadOAuthConfig = async () => {
      if (sourceId !== 'email' || !isOpen || oauthConfigLoaded) return;

      try {
        const response = await fetch('/api/auth/config');
        if (response.ok) {
          const config = await response.json();
          if (config.configured) {
            setOauthConfig({
              clientId: config.clientId,
              redirectUri: config.redirectUri,
              tenantId: config.tenantId || 'common'
            });
          }
        } else {
          console.warn('OAuth config endpoint returned:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('Failed to load OAuth config:', error);
      } finally {
        setOauthConfigLoaded(true);
      }
    };

    loadOAuthConfig();
  }, [sourceId, isOpen, oauthConfigLoaded]);

  // Auto-detect market data connection and update processing state (following OAuth pattern)
  useEffect(() => {
    if (sourceId === 'market' && isMarketDataConnected && processingState[sourceId] !== 'complete') {
      setProcessingState((prev: Record<string, 'idle' | 'processing' | 'complete'>) => ({ ...prev, [sourceId]: 'complete' }));
      setConnectedSources((prev: string[]) => {
        if (!prev.includes(sourceId)) {
          return [...prev, sourceId];
        }
        return prev;
      });
      
      // Update market stats immediately
      setMarketStats({
        isConnected: true,
        lastQuoteTime: new Date().toISOString(),
        quotesThisHour: 0,
        rateLimitRemaining: 60
      });
    }
  }, [sourceId, isMarketDataConnected, processingState, setConnectedSources, setMarketStats]);

  // Auto-detect OAuth connection and update processing state
  useEffect(() => {
    if (sourceId === 'email' && isOAuthConnected && processingState[sourceId] !== 'complete') {
      setProcessingState((prev: Record<string, 'idle' | 'processing' | 'complete'>) => ({ ...prev, [sourceId]: 'complete' }));
      setConnectedSources((prev: string[]) => {
        if (!prev.includes(sourceId)) {
          return [...prev, sourceId];
        }
        return prev;
      });
    }
  }, [sourceId, isOAuthConnected, processingState, setConnectedSources]);


  // Cleanup auto-advance timer on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimer) {
        clearTimeout(autoAdvanceTimer);
      }
    };
  }, [autoAdvanceTimer]);

  // Cleanup all active polling intervals on unmount
  useEffect(() => {
    return () => {
      // Clear all active polling intervals
      activePollingIntervals.current.forEach(interval => {
        clearInterval(interval);
      });
      activePollingIntervals.current.clear();
    };
  }, []);

  const handleFileUpload = async (type: 'excel' | 'pdf', files?: FileList | null) => {
    // Check if client is selected
    if (!selectedClient) {
      setUploadError('Please select a client before uploading documents');
      return;
    }

    // Import upload service dynamically
    const { uploadService } = await import('../../services/uploadService');

    try {
      setProcessingState(prev => ({ ...prev, [sourceId]: 'processing' }));
      setUploadStatus('uploading');
      setUploadError(null);

      let uploadId: string | null = null;

      if (files) {
        // Handle multiple file uploads with client ID
        const uploadIds = await uploadService.handleMultipleFileInput(
          files,
          (uploadId, progress) => {
            setUploadProgress(prev => ({ ...prev, [uploadId || sourceId]: progress.progress }));

            if (progress.status === 'processing') {
              setUploadStatus('processing');
              setProcessingState(prev => ({ ...prev, [sourceId]: 'processing' }));
            } else if (progress.status === 'error') {
              setUploadStatus('error');
              setUploadError(progress.error || 'Upload failed');
              setProcessingState(prev => ({ ...prev, [sourceId]: 'idle' }));
            }
          },
          selectedClient.id, // Pass client ID
          type === 'excel' ? ['.xlsx', '.xls', '.csv'] : ['.pdf']
        );

        // For now, track the first upload ID for compatibility
        uploadId = uploadIds.length > 0 ? uploadIds[0] : null;
      } else {
        // Trigger file picker
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true; // Allow multiple file selection
        input.accept = type === 'excel' ? '.xlsx,.xls,.csv' : '.pdf';
        input.onchange = async (e) => {
          const target = e.target as HTMLInputElement;
          if (target.files && target.files.length > 0) {
            await handleFileUpload(type, target.files);
          }
        };
        input.click();
        return;
      }

      if (uploadId) {
        setUploadId(uploadId);

        // Set up WebSocket for real-time progress updates
        const ws = uploadService.createProgressWebSocket(
          uploadId,
          (data) => {
            if (data.status === 'ready') {
              setUploadStatus('ready');
              setProcessingState((prev: Record<string, 'idle' | 'processing' | 'complete'>) => ({ ...prev, [sourceId]: 'complete' }));
              setConnectedSources((prev: string[]) => {
                if (!prev.includes(sourceId)) {
                  return [...prev, sourceId];
                }
                return prev;
              });

              // Load preview data
              uploadService.getPreview(uploadId).then(preview => {
                setPreviewData(preview);
              }).catch(err => {
                console.error('Failed to load preview:', err);
              });

              // Auto-advance to review screen after processing is complete
              const timer = setTimeout(() => {
                onComplete();
              }, 2000);
              setAutoAdvanceTimer(timer);
            } else if (data.status === 'complete') {
              setUploadStatus('ready');
              setProcessingState(prev => ({ ...prev, [sourceId]: 'complete' }));

              // Auto-advance timer
              const timer = setTimeout(() => {
                onComplete();
              }, 2000);
              setAutoAdvanceTimer(timer);
            } else if (data.status === 'error') {
              setUploadStatus('error');
              setUploadError(data.message || 'Processing failed');
              setProcessingState(prev => ({ ...prev, [sourceId]: 'idle' }));
            }
          },
          (error) => {
            console.error('WebSocket error:', error);
            setUploadStatus('error');
            setUploadError('Connection error');
          }
        );

        // Fallback: Poll for status if WebSocket fails
        let statusPollInterval: NodeJS.Timeout | null = null;
        let pollCleanupTimeout: NodeJS.Timeout | null = null;

        const startPolling = () => {
          console.log(`Starting status polling for upload ID: ${uploadId}`);
          statusPollInterval = setInterval(async () => {
            try {
              console.log(`Polling status for upload ID: ${uploadId}`);
              const status = await uploadService.getUploadStatus(uploadId);
              console.log(`Upload ${uploadId} status:`, status);

              if (status.status === 'ready') {
                setUploadStatus('ready');
                setProcessingState((prev: Record<string, 'idle' | 'processing' | 'complete'>) => ({ ...prev, [sourceId]: 'complete' }));
                setConnectedSources((prev: string[]) => {
                if (!prev.includes(sourceId)) {
                  return [...prev, sourceId];
                }
                return prev;
              });

                // Load preview data
                uploadService.getPreview(uploadId).then(preview => {
                  setPreviewData(preview);
                }).catch(err => {
                  console.error('Failed to load preview:', err);
                });

                // Auto-advance to review screen
                const timer = setTimeout(() => {
                  onComplete();
                }, 2000);
                setAutoAdvanceTimer(timer);

                // Clear polling
                if (statusPollInterval) {
                  clearInterval(statusPollInterval);
                  activePollingIntervals.current.delete(statusPollInterval);
                  statusPollInterval = null;
                }
                if (pollCleanupTimeout) {
                  clearTimeout(pollCleanupTimeout);
                  pollCleanupTimeout = null;
                }
              } else if (status.status === 'error') {
                setUploadStatus('error');
                setUploadError(status.error_message || 'Processing failed');
                setProcessingState(prev => ({ ...prev, [sourceId]: 'idle' }));

                // Clear polling
                if (statusPollInterval) {
                  clearInterval(statusPollInterval);
                  activePollingIntervals.current.delete(statusPollInterval);
                  statusPollInterval = null;
                }
                if (pollCleanupTimeout) {
                  clearTimeout(pollCleanupTimeout);
                  pollCleanupTimeout = null;
                }
              }
            } catch (error) {
              console.error(`Status polling error for ${uploadId}:`, error);
            }
          }, 2000); // Poll every 2 seconds
        };

        // Start polling immediately as fallback
        startPolling();

        // Track the interval for cleanup
        if (statusPollInterval) {
          activePollingIntervals.current.add(statusPollInterval);
        }

        // Clean up polling after 30 seconds if not already cleared
        pollCleanupTimeout = setTimeout(() => {
          console.log(`Stopping polling for upload ID: ${uploadId} after 30 seconds`);
          if (statusPollInterval) {
            clearInterval(statusPollInterval);
            activePollingIntervals.current.delete(statusPollInterval);
            statusPollInterval = null;
          }
        }, 30000);

        // Store WebSocket for cleanup
        return () => ws.close();
      }

    } catch (error) {
      setUploadStatus('error');
      setUploadError(error instanceof Error ? error.message : 'Upload failed');
      setProcessingState(prev => ({ ...prev, [sourceId]: 'idle' }));
    }
  };

  const handleProviderConnect = async () => {
    // Mock implementation for CRM and other providers
    setProcessingState(prev => ({ ...prev, [sourceId]: 'processing' }));

    setTimeout(() => {
      setProcessingState(prev => ({ ...prev, [sourceId]: 'complete' }));
      setConnectedSources((prev: string[]) => [...prev, sourceId]);
    }, 2500);
  };

  const renderSidePanelContent = (sourceId: string) => {
    switch (sourceId) {
      case 'excel':
        return (
          <>
            {/* Client Selection */}
            <div className="mb-6">
              <ClientSelector 
                required={true}
                onClientSelected={() => {
                  // Client selected, refresh to show upload interface
                }}
              />
            </div>
            
            {/* Upload section - visible when client is selected */}
            {processingState[sourceId] === 'processing' ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-kielis-gold border-t-transparent mb-4"></div>
                <h3 className="font-medium mb-2">
                  {uploadStatus === 'uploading' ? 'Uploading file...' : 
                   uploadStatus === 'processing' ? 'Processing Excel file...' : 
                   'Processing...'}
                </h3>
                {uploadProgress[sourceId] > 0 && (
                  <div className="mt-4">
                    <div className="bg-gray-200 rounded-full h-2 w-full">
                      <div 
                        className="bg-kielis-gold h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress[sourceId] || 0}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-600 mt-2">{uploadProgress[sourceId]}% complete</p>
                  </div>
                )}
                {uploadError && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm">{uploadError}</p>
                    <button 
                      onClick={() => {
                        setUploadError(null);
                        setProcessingState(prev => ({ ...prev, [sourceId]: 'idle' }));
                      }}
                      className="mt-2 text-red-600 underline text-sm"
                    >
                      Try again
                    </button>
                  </div>
                )}
              </div>
            ) : processingState[sourceId] === 'complete' ? (
              <div className="text-center py-8">
                <div className="text-5xl mb-4">✅</div>
                <h3 className="font-medium mb-2">Upload complete!</h3>
                <p className="text-kielis-success">
                  {previewData ? `${previewData.total_rows} rows processed` : '247 positions imported'}
                </p>
                {previewData && previewData.error_percentage > 0 && (
                  <p className="text-orange-600 text-sm mt-2">
                    {previewData.validation_errors?.length || 0} data quality issues found ({previewData.error_percentage.toFixed(1)}%)
                  </p>
                )}
              </div>
            ) : (
              <div 
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                  selectedClient 
                    ? 'border-gray-300 hover:border-kielis-navy' 
                    : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                }`}
                onClick={() => selectedClient && handleFileUpload('excel')}
                onDrop={async (e) => {
                  if (!selectedClient) {
                    e.preventDefault();
                    setUploadError('Please select a client before uploading documents');
                    return;
                  }
                  e.preventDefault();
                  const { uploadService } = await import('../../services/uploadService');
                  try {
                    setProcessingState(prev => ({ ...prev, [sourceId]: 'processing' }));
                    const uploadId = await uploadService.handleDrop(
                      e,
                      (progress) => {
                        setUploadProgress(prev => ({ ...prev, [sourceId]: progress.progress }));
                        if (progress.status === 'processing') {
                          setUploadStatus('processing');
                        } else if (progress.status === 'error') {
                          setUploadStatus('error');
                          setUploadError(progress.error || 'Upload failed');
                          setProcessingState(prev => ({ ...prev, [sourceId]: 'idle' }));
                        }
                      },
                      selectedClient?.id || '', // Pass client ID if available
                      ['.xlsx', '.xls', '.csv']
                    );
                    if (uploadId) {
                      setUploadId(uploadId);
                      setUploadStatus('processing');
                    }
                  } catch (error) {
                    setUploadStatus('error');
                    setUploadError(error instanceof Error ? error.message : 'Upload failed');
                    setProcessingState(prev => ({ ...prev, [sourceId]: 'idle' }));
                  }
                }}
                onDragOver={(e) => e.preventDefault()}
                onDragEnter={(e) => e.preventDefault()}
              >
                <div className="text-5xl mb-4">📊</div>
                <h3 className="font-medium mb-2">
                  {selectedClient ? 'Upload your Excel file' : 'Select a client first'}
                </h3>
                <p className="text-gray-600 text-sm">
                  {selectedClient ? 'Drag & drop or click to browse' : 'Client selection required before upload'}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  {selectedClient && 'Supports: .xlsx, .xls, .csv (up to 1GB)'}
                </p>
              </div>
            )}
            
            {/* Info box */}
            {selectedClient && (
              <div className="mt-4 p-4 bg-kielis-glacier rounded-lg text-sm">
                <strong>What we'll import for {selectedClient.name}:</strong><br />
                • Client positions and holdings<br />
                • Asset allocations and values<br />
                • Account information
              </div>
            )}
          </>
        );

      case 'email':
        return (
          <>
            {processingState[sourceId] === 'processing' ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-kielis-gold border-t-transparent mb-4"></div>
                <h3 className="font-medium mb-2">Connecting to email provider...</h3>
                <p className="text-gray-600 text-sm">Authenticating and setting up secure access</p>
              </div>
            ) : processingState[sourceId] === 'complete' && isOAuthConnected ? (
              <div className="space-y-6">
                {/* Connection Success Header */}
                <div className="text-center">
                  <div className="text-5xl mb-4">✅</div>
                  <h3 className="font-medium mb-2">Connected successfully!</h3>
                  <p className="text-kielis-success">Microsoft 365 access configured</p>
                  {oauthUser && (
                    <div className="mt-4 p-4 bg-green-50 rounded-lg">
                      <div className="text-sm font-medium text-green-800">
                        Connected as: {oauthUser.display_name}
                      </div>
                      <div className="text-sm text-green-700">
                        {oauthUser.email}
                      </div>
                    </div>
                  )}
                </div>

                {/* Disconnect Option */}
                <div className="text-center border-t pt-4">
                  <button
                    onClick={() => disconnectOAuth()}
                    className="text-sm text-red-600 hover:text-red-700 underline"
                  >
                    Disconnect Microsoft 365
                  </button>
                </div>
              </div>
            ) : showOAuthConfig ? (
              <div className="space-y-6">
                {/* Back Button */}
                <button
                  onClick={() => setShowOAuthConfig(false)}
                  className="flex items-center text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  ← Back to provider selection
                </button>

                {/* OAuth Configuration Section */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-2">📋 Microsoft OAuth Configuration</h4>
                  <p className="text-sm text-blue-700 mb-4">
                    To connect to Microsoft 365, you need to provide your OAuth application credentials.
                  </p>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client ID
                      </label>
                      <input
                        type="text"
                        placeholder="Enter your Microsoft Client ID"
                        value={oauthConfig.clientId}
                        onChange={(e) => setOauthConfig(prev => ({ ...prev, clientId: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Redirect URI
                      </label>
                      <input
                        type="text"
                        value={oauthConfig.redirectUri}
                        onChange={(e) => setOauthConfig(prev => ({ ...prev, redirectUri: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tenant ID
                      </label>
                      <input
                        type="text"
                        placeholder="common (or your tenant ID)"
                        value={oauthConfig.tenantId}
                        onChange={(e) => setOauthConfig(prev => ({ ...prev, tenantId: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Use "common" for multi-tenant apps, or your specific tenant ID for single-tenant apps
                      </p>
                    </div>
                    
                    <div className="space-y-3">
                      <button
                        onClick={async () => {
                          try {
                            // Save OAuth configuration
                            const response = await fetch('/api/auth/config', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify(oauthConfig)
                            });
                            
                            if (response.ok) {
                              // Configuration saved successfully, now initiate OAuth connection
                              setProcessingState(prev => ({ ...prev, [sourceId]: 'processing' }));
                              clearOAuthError();
                              clearDataPreviewError();
                              
                              // Connect to Microsoft 365 using OAuth
                              await connectOAuth();
                              
                              // Fetch data preview after successful connection
                              console.log('Fetching data preview after OAuth connection...');
                              await fetchDataPreview();
                              
                              // Processing state will be updated automatically by useEffect when isOAuthConnected becomes true
                            } else {
                              console.error('Failed to save OAuth config');
                            }
                          } catch (error) {
                            console.error('Failed to save OAuth config or connect:', error);
                            setProcessingState(prev => ({ ...prev, [sourceId]: 'idle' }));
                          }
                        }}
                        disabled={!oauthConfig.clientId.trim() || isOAuthConnecting}
                        className="w-full bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                      >
                        {isOAuthConnecting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Connecting...
                          </>
                        ) : (
                          'Connect to Microsoft 365'
                        )}
                      </button>
                      
                      {oauthError && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-red-600 text-sm">{oauthError}</p>
                          <button 
                            onClick={clearOAuthError}
                            className="mt-2 text-red-600 underline text-sm"
                          >
                            Dismiss
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <div className="text-xs text-gray-600">
                      <strong>How to get these credentials:</strong><br />
                      1. Go to <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Azure Portal</a><br />
                      2. Navigate to "App registrations"<br />
                      3. Create a new registration or use existing<br />
                      4. Copy the "Application (client) ID"<br />
                      5. Add redirect URI in "Authentication" section
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900 mb-4">Choose your email provider:</h3>
                  
                  {/* Microsoft 365 Option */}
                  <div 
                    onClick={() => setShowOAuthConfig(true)}
                    className="border border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50 cursor-pointer transition-all"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="bg-blue-600 text-white p-2 rounded">📧</div>
                      <div>
                        <h4 className="font-medium text-gray-900">Microsoft 365</h4>
                        <p className="text-sm text-gray-600">Outlook, Exchange, Teams</p>
                      </div>
                    </div>
                  </div>

                  {/* Google Workspace Option (Coming Soon) */}
                  <div className="border border-gray-200 rounded-lg p-4 opacity-50 cursor-not-allowed">
                    <div className="flex items-center space-x-3">
                      <div className="bg-red-600 text-white p-2 rounded">📬</div>
                      <div>
                        <h4 className="font-medium text-gray-900">Google Workspace</h4>
                        <p className="text-sm text-gray-600">Gmail, Calendar, Drive (Coming Soon)</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
                  <strong>What we'll access:</strong><br />
                  • Email for meeting context<br />
                  • Calendar events and invitations<br />
                  • Meeting attendee information<br />
                  • Client communication history
                </div>
              </>
            )}
          </>
        );

      case 'pdf':
        return (
          <>
            {/* Client Selection */}
            <div className="mb-6">
              <ClientSelector 
                required={true}
                onClientSelected={() => {
                  // Client selected, refresh to show upload interface
                }}
              />
            </div>
            
            {/* Upload section */}
            {processingState[sourceId] === 'processing' ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-kielis-gold border-t-transparent mb-4"></div>
                <h3 className="font-medium mb-2">Processing PDFs...</h3>
              </div>
            ) : processingState[sourceId] === 'complete' ? (
              <div className="text-center py-8">
                <div className="text-5xl mb-4">✅</div>
                <h3 className="font-medium mb-2">PDFs processed!</h3>
                <p className="text-kielis-success">Documents analyzed with enhanced extraction</p>
              </div>
            ) : (
              <div 
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                  selectedClient 
                    ? 'border-gray-300 hover:border-kielis-navy' 
                    : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                }`}
                onClick={() => selectedClient && handleFileUpload('pdf')}
              >
                <div className="text-5xl mb-4">📄</div>
                <h3 className="font-medium mb-2">
                  {selectedClient ? 'Upload PDF documents' : 'Select a client first'}
                </h3>
                <p className="text-gray-600 text-sm">
                  {selectedClient ? 'Bank statements, fund reports, etc.' : 'Client selection required before upload'}
                </p>
              </div>
            )}
            
            {/* Info box */}
            {selectedClient && (
              <div className="mt-4 p-4 bg-kielis-glacier rounded-lg text-sm">
                <strong>We can extract for {selectedClient.name}:</strong><br />
                • Transaction histories<br />
                • Account balances<br />
                • Performance data<br />
                • Fee information
              </div>
            )}
          </>
        );

      case 'market':
        return (
          <>
            {processingState[sourceId] === 'processing' ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-kielis-gold border-t-transparent mb-4"></div>
                <h3 className="font-medium mb-2">Connecting to Finnhub...</h3>
                <p className="text-gray-600 text-sm">Testing API connection and setting up market data feeds</p>
              </div>
            ) : processingState[sourceId] === 'complete' && isMarketDataConnected ? (
              <div className="space-y-6">
                {/* Connection Success Header */}
                <div className="text-center">
                  <div className="text-5xl mb-4">✅</div>
                  <h3 className="font-medium mb-2">Finnhub Connected!</h3>
                  <p className="text-kielis-success">Real-time market data active</p>
                  {marketDataConnectionConfig?.apiKey && (
                    <div className="mt-4 p-4 bg-green-50 rounded-lg">
                      <div className="text-sm font-medium text-green-800">
                        API Key: ****{marketDataConnectionConfig.apiKey.slice(-4)}
                      </div>
                      <div className="text-sm text-green-700">
                        Plan: {marketDataConnectionConfig.planTier.charAt(0).toUpperCase() + marketDataConnectionConfig.planTier.slice(1)}
                      </div>
                    </div>
                  )}
                </div>

                {/* Connected Features */}
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-sm text-green-800">
                    <strong>Connected Features:</strong><br />
                    • Real-time stock quotes<br />
                    • Company news and sentiment<br />
                    • Financial fundamentals<br />
                    • Global market coverage
                  </div>
                </div>

                {/* Disconnect Option */}
                <div className="text-center border-t pt-4">
                  <button
                    onClick={() => {
                      disconnectMarketData().then(() => {
                        setProcessingState(prev => ({ ...prev, [sourceId]: 'idle' }));
                        setConnectedSources((prev: string[]) => prev.filter((s: string) => s !== sourceId));
                        setMarketStats({
                          isConnected: false,
                          lastQuoteTime: null,
                          quotesThisHour: 0,
                          rateLimitRemaining: 60
                        });
                      }).catch(console.error);
                    }}
                    className="text-sm text-red-600 hover:text-red-700 underline"
                  >
                    Disconnect Finnhub API
                  </button>
                </div>
              </div>
            ) : showMarketDataConfig ? (
              <div className="space-y-6">
                {/* Back Button */}
                <button
                  onClick={() => setShowMarketDataConfig(false)}
                  className="flex items-center text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  ← Back to market data setup
                </button>

                {/* Finnhub Configuration Section */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-800 mb-2">📈 Finnhub API Configuration</h4>
                  <p className="text-sm text-yellow-700 mb-4">
                    Connect to Finnhub for real-time stock quotes, news, and financial data during client meetings.
                  </p>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        API Key *
                      </label>
                      <input
                        type="text"
                        placeholder="Enter your Finnhub API key"
                        value={marketDataConfig.apiKey}
                        onChange={(e) => setMarketDataConfig(prev => ({ ...prev, apiKey: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Plan Tier
                      </label>
                      <select
                        value={marketDataConfig.planTier}
                        onChange={(e) => setMarketDataConfig(prev => ({ ...prev, planTier: e.target.value as 'free' | 'basic' | 'premium' }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                      >
                        <option value="free">Free (60 calls/min)</option>
                        <option value="basic">Basic (300 calls/min)</option>
                        <option value="premium">Premium (1000 calls/min)</option>
                      </select>
                    </div>
                    
                    <div className="space-y-3">
                      <button
                        onClick={async () => {
                          try {
                            clearMarketDataError();
                            await connectMarketData(marketDataConfig);
                            // Hook will automatically update the connection state
                          } catch (error) {
                            console.error('Market data connection failed:', error);
                            // Error is handled by the hook
                          }
                        }}
                        disabled={!marketDataConfig.apiKey?.trim() || isMarketDataConnecting}
                        className="w-full bg-yellow-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-yellow-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                      >
                        {isMarketDataConnecting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Connecting...
                          </>
                        ) : (
                          'Connect to Finnhub'
                        )}
                      </button>
                      
                      {marketDataError && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-red-600 text-sm">{marketDataError}</p>
                          <button 
                            onClick={clearMarketDataError}
                            className="mt-2 text-red-600 underline text-sm"
                          >
                            Dismiss
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <div className="text-xs text-gray-600">
                      <strong>How to get your API key:</strong><br />
                      1. Sign up at <a href="https://finnhub.io" target="_blank" rel="noopener noreferrer" className="text-yellow-600 underline">finnhub.io</a><br />
                      2. Verify your email address<br />
                      3. Copy your API key from the dashboard<br />
                      4. Free tier includes 60 calls/minute
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  <h3 className="font-medium text-gray-900 mb-4">Setup Finnhub Market Data:</h3>
                  
                  {/* Finnhub Setup Option */}
                  <div 
                    onClick={() => setShowMarketDataConfig(true)}
                    className="border border-gray-200 rounded-lg p-4 hover:border-yellow-500 hover:bg-yellow-50 cursor-pointer transition-all"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="bg-yellow-600 text-white p-2 rounded">📈</div>
                      <div>
                        <h4 className="font-medium text-gray-900">Finnhub API</h4>
                        <p className="text-sm text-gray-600">Real-time quotes, news, and fundamentals</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
                  <strong>What you'll get:</strong><br />
                  • Real-time stock quotes during meetings<br />
                  • Company news and sentiment analysis<br />
                  • Financial fundamentals and metrics<br />
                  • Global market coverage (US, EU, Asia)
                </div>
              </>
            )}
          </>
        );

      case 'crm':
        return (
          <>
            {processingState[sourceId] === 'processing' ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-kielis-gold border-t-transparent mb-4"></div>
                <h3 className="font-medium mb-2">Connecting to CRM...</h3>
                <p className="text-gray-600 text-sm">Setting up data synchronization</p>
              </div>
            ) : processingState[sourceId] === 'complete' ? (
              <div className="text-center py-8">
                <div className="text-5xl mb-4">✅</div>
                <h3 className="font-medium mb-2">CRM connected!</h3>
                <p className="text-kielis-success">Two-way sync enabled</p>
                <div className="mt-4 text-sm text-gray-600">
                  • 127 client records synced<br />
                  • Meeting notes integration active<br />
                  • Contact updates automated
                </div>
              </div>
            ) : (
              <>
                <h4 className="font-medium mb-4">Choose your CRM system:</h4>
                {crmProviders.map(provider => (
                  <div 
                    key={provider.id}
                    className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg mb-3 cursor-pointer hover:border-kielis-navy transition-all"
                    onClick={() => handleProviderConnect()}
                  >
                    <div className={`w-12 h-12 ${provider.bgColor} text-white rounded-lg flex items-center justify-center text-2xl`}>
                      {provider.icon}
                    </div>
                    <div>
                      <div className="font-medium">{provider.name}</div>
                      <div className="text-sm text-gray-600">{provider.description}</div>
                    </div>
                  </div>
                ))}
                <div className="mt-4 p-3 bg-green-50 rounded-lg text-sm">
                  🔄 <strong>Two-way sync</strong> - Client data and meeting notes stay in sync
                </div>
              </>
            )}
          </>
        );

      default:
        return <p className="text-gray-600">Source configuration not available yet.</p>;
    }
  };

  if (!isOpen || !source) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 w-96 h-full bg-white shadow-xl z-50 transform transition-transform">
        <div className="bg-kielis-navy text-white p-4">
          <div className="flex justify-between items-start">
            <button 
              className="text-white opacity-70 hover:opacity-100"
              onClick={onClose}
            >
              ← Back to Hub
            </button>
            <div className="text-center flex-1">
              <h4 className="font-medium">
                <span className={`inline-flex items-center justify-center w-6 h-6 ${source.bgColor} text-white rounded mr-2 text-sm`}>
                  {source.icon}
                </span>
                Step {stepNum} of {totalSteps} - {source.title}
              </h4>
              {nextSourceTitle && (
                <div className="text-sm opacity-70 mt-1">
                  Next: {nextSourceTitle}
                </div>
              )}
            </div>
            <button 
              className="text-white opacity-70 hover:opacity-100 text-xl"
              onClick={onClose}
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6 h-[calc(100vh-180px)] overflow-y-auto">
          {/* TASK-630: fresh tenant has zero clients — uploads need one, so offer
              inline creation (createClient auto-selects the new client). */}
          {clients.length === 0 && !selectedClient && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              {showCreateClient ? (
                <>
                  <p className="text-sm font-medium text-gray-900 mb-3">Create your first client</p>
                  <ClientCreateForm
                    onClientCreated={() => setShowCreateClient(false)}
                  />
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-800 mb-2">
                    You don't have any clients yet — document uploads are attached to a client.
                  </p>
                  <button
                    className="bg-kielis-navy text-white px-3 py-1.5 rounded text-sm font-medium hover:bg-opacity-90"
                    onClick={() => setShowCreateClient(true)}
                  >
                    + Create your first client
                  </button>
                </>
              )}
            </div>
          )}
          {renderSidePanelContent(sourceId)}
        </div>

        <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex gap-4">
          <button 
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded font-medium hover:bg-gray-200 transition-all"
            onClick={onSkip}
          >
            Skip
          </button>
          <button 
            className="bg-kielis-navy text-white px-6 py-2 rounded font-medium hover:bg-opacity-90 transition-all flex items-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
            onClick={onComplete}
            disabled={
              processingState[sourceId] !== 'complete' && 
              !(sourceId === 'email' && isOAuthConnected)
            }
          >
            Next
            <span className="text-xs opacity-70">⌘↵</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default DataSourceWizard;